import {useEffect, useRef} from 'react'
import {useAnnouncer} from '@/components/shared/Announcer'

/**
 * Announce the new step name AND move focus into the step container on every
 * `step` change. Pair with the returned `ref` placed on the wrapper that
 * surrounds the step's content. Issue 800.
 *
 * <p>In-page step transitions are not route changes, so
 * {@code useFocusOnRouteChange} does not fire — without this hook, focus
 * sticks on the just-clicked Next button (which then unmounts) and SR users
 * lose orientation. The hook announces the new step's label via the polite
 * live region, then schedules a {@code requestAnimationFrame} focus shift
 * onto the first heading or focusable element inside the container.
 *
 * <p>The first render is skipped so initial page entry doesn't steal focus
 * from whatever element brought the user to the wizard.
 */
export function useStepFocus<E extends HTMLElement = HTMLDivElement>(step: number, stepLabel: string) {
    const {announce} = useAnnouncer()
    const ref = useRef<E>(null)
    // WHY: a boolean "didMount" ref breaks under React.StrictMode's
    // setup→cleanup→setup double-invoke — the second setup sees the ref
    // already flipped and steals focus from the route-change H1 target on
    // initial mount. Track the previous step instead so the no-op compare
    // is value-based and StrictMode-safe.
    const prevStepRef = useRef(step)

    useEffect(() => {
        if (prevStepRef.current === step) return
        prevStepRef.current = step
        announce(stepLabel)
        const frame = requestAnimationFrame(() => {
            const container = ref.current
            if (!container) return
            // eslint-disable-next-line i18next/no-literal-string -- CSS selectors
            const heading = container.querySelector('h2, h3') as HTMLElement | null
            // eslint-disable-next-line i18next/no-literal-string -- CSS selector list
            const fallback = container.querySelector('button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') as HTMLElement | null
            const target = heading ?? fallback
            if (!target) return
            // eslint-disable-next-line i18next/no-literal-string -- DOM attribute
            if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
            target.focus({preventScroll: false} as FocusOptions)
        })
        return () => cancelAnimationFrame(frame)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depend only on the step index.
    }, [step])

    return ref
}
